import { useEffect, useState } from "react";
import { Zap, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls } from "@/lib/studioApi";

export const MollieSettings = () => {
  const [state, setState] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { studio.get("/profile/mollie").then(({ data }) => setState(data)).catch(() => setState({ enabled: false })); }, []);

  const save = async (apiKey) => {
    setBusy(true);
    try {
      const { data } = await studio.post("/profile/mollie", { api_key: apiKey });
      setState(data); setKey("");
      toast.success(apiKey ? `Mollie activé (${data.mode}) — ${data.labels.join(", ") || "méthodes à activer dans Mollie"}` : "Mollie désactivé.");
    } catch (e) { toast.error(apiError(e, "Clé refusée par Mollie.")); } finally { setBusy(false); }
  };

  return (
    <div data-testid="profile-mollie" className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
      <p className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F2645]"><Zap className="h-4 w-4 text-[#3A5CC8]" /> Paiement en ligne des factures (Mollie)</p>
      <p className="mt-1 text-xs text-[#5C6A87] leading-relaxed">
        Vos clients paient par Bancontact, Payconiq ou virement depuis le bouton « Payer maintenant » de l'e-mail : leur app bancaire s'ouvre pré-remplie et la facture passe <b>payée</b> toute seule.
      </p>
      <p data-testid="mollie-payout-info" className="mt-2 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] px-3 py-2 text-xs leading-relaxed text-[#0F2645]">
        <b>Où va l'argent ?</b> Directement sur <b>votre</b> compte Mollie, puis viré sur <b>votre compte bancaire</b> (chaque jour ouvrable par défaut, réglable dans le Dashboard Mollie → Virements). Devisto ne détient jamais vos fonds et ne prend aucune commission : seuls les frais Mollie s'appliquent (≈ 0,39 € par paiement Bancontact, sans abonnement). Pour recevoir les virements, votre compte Mollie doit être vérifié (identité + IBAN).
      </p>
      {state?.enabled ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span data-testid="mollie-status" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Actif · clé {state.masked} · mode {state.mode === "live" ? "réel" : "test"}</span>
          <span className="text-xs text-[#5C6A87]">{state.labels?.length ? `Méthodes : ${state.labels.join(", ")}` : "Activez Bancontact/Payconiq dans votre Dashboard Mollie."}</span>
          <button type="button" onClick={() => save("")} disabled={busy} data-testid="mollie-disable" className="text-xs font-semibold text-rose-600 hover:underline">Désactiver</button>
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input data-testid="mollie-key" type="password" className={`${inputCls} flex-1`} placeholder="Clé API Mollie (test_… ou live_…)" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" />
          <button type="button" onClick={() => save(key)} disabled={busy || !key.trim()} data-testid="mollie-save" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F2645] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Vérifier et activer
          </button>
        </div>
      )}
      <a href="https://my.mollie.com/dashboard/developers/api-keys" target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#3A5CC8] hover:underline">
        Créer un compte Mollie gratuit et copier la clé API <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
};
